import { PORTAL_NAME } from '@/lib/branding'
import { CHARGEE_ENTITY as C, type TermsDocument } from './types'

/**
 * Toestemming voor het delen van gegevens met Netbeheer Nederland in de pilot
 * Net bewust thuis laden, revisie 21 oktober 2026. Vult de Privacyverklaring
 * van Chargee aan, die het delen met netbeheerders nog niet beschrijft.
 */
export const DATA_SHARING_TERMS: TermsDocument = {
  title: 'Voorwaarden voor het delen van gegevens met Netbeheer Nederland',
  version: '2026-10-21',
  revisionDate: '2026-10-21',
  intro: [
    `Voor de pilot Net bewust thuis laden deelt ${C.name} gegevens over je deelname met Netbeheer Nederland. Daarvoor vragen we je apart om toestemming. Hieronder lees je welke gegevens dat zijn, waarom we ze delen, hoe lang ze bewaard worden en hoe je je toestemming intrekt.`,
    'Deze voorwaarden vullen de Privacyverklaring van Chargee aan. Wat hier niet anders staat, zoals hoe je je privacyrechten uitoefent, regelt de Privacyverklaring.',
  ],
  articles: [
    {
      id: 'waarom',
      title: 'Waarom we gegevens delen',
      blocks: [
        'De pilot onderzoekt of huishoudens het stroomnet ontlasten door hun auto niet thuis te laden tussen 16:00 en 21:00 uur, als het net het drukst is. Netbeheer Nederland, de vereniging van netbeheerders in Nederland, en de netbeheerder in jouw regio willen kunnen vaststellen:',
        [
          'welke huishoudens hebben meegedaan en in welke periode;',
          'of er in het piekvenster thuis is geladen;',
          'welk effect dat heeft gehad op de belasting van het stroomnet in de regio.',
        ],
        'Op basis daarvan bevestigt de netbeheerder de resultaten van de pilot. Die bevestiging is nodig om de beloning uit te betalen.',
      ],
    },
    {
      id: 'welke-gegevens',
      title: 'Welke gegevens we delen',
      blocks: [
        'We delen alleen wat voor de doelen hierboven nodig is:',
        [
          'een deelnemersnummer dat Chargee voor de pilot aanmaakt; je naam, e-mailadres, huisnummer en accountgegevens delen we niet;',
          'de postcode van het deelnemende adres, om het adres aan het juiste deel van het stroomnet te kunnen koppelen;',
          'je deelnameperiode en de status van je deelname, zoals aangemeld, actief, gestopt of uitgeschreven;',
          'per dag of er in het piekvenster thuis is geladen;',
          'het elektriciteitsverbruik van het net op het adres per uur tijdens het piekvenster, zoals gemeten door Sparky of Flint;',
          'laadsessies van de gekoppelde laadpalen en auto’s die (deels) in het piekvenster vallen: begin, einde en geladen hoeveelheid energie.',
        ],
        'We delen geen gegevens van buiten de pilotperiode, geen verbruik buiten het piekvenster en geen locatiegegevens van je auto.',
      ],
    },
    {
      id: 'grondslag',
      title: 'Grondslag: jouw toestemming',
      blocks: [
        'We delen deze gegevens op basis van jouw toestemming (artikel 6, eerste lid, onder a, van de Algemene verordening gegevensbescherming). Je geeft deze toestemming apart van je akkoord met de Voorwaarden van Net bewust thuis laden, met een eigen vinkje.',
        'Zonder deze toestemming kun je niet aan de pilot deelnemen, omdat de netbeheerder de resultaten dan niet kan bevestigen en er geen beloning kan worden uitbetaald. Je deelname aan de pilot en je toestemming zijn vrijwillig; je overige gebruik van Chargee hangt er niet van af.',
      ],
    },
    {
      id: 'wie-ontvangt',
      title: 'Wie de gegevens ontvangt',
      blocks: [
        `${C.name} is verantwoordelijk voor het verzamelen van de gegevens en het delen ervan. Ontvangers zijn Netbeheer Nederland en de netbeheerder die verantwoordelijk is voor het stroomnet op het deelnemende adres. Zij gebruiken de gegevens alleen voor de doelen in deze voorwaarden, en zijn zelf verantwoordelijk voor hun verwerking; daarover lees je meer in hun eigen privacyverklaringen.`,
        'We delen de gegevens via een beveiligde verbinding. De gegevens worden binnen de Europese Economische Ruimte verwerkt.',
      ],
    },
    {
      id: 'wanneer',
      title: 'Wanneer we gegevens delen',
      blocks: [
        'We delen de gegevens maandelijks tijdens de pilotperiode en eenmalig na afloop van de pilot, voor de bevestiging van de resultaten en de uitbetaling van de beloning.',
      ],
    },
    {
      id: 'bewaren',
      title: 'Hoe lang de gegevens bewaard worden',
      blocks: [
        'Chargee bewaart de gegevens over je deelname tot twee jaar na het einde van de pilot, zodat we vragen, klachten en controles over de beloning kunnen afhandelen. Daarna verwijderen of anonimiseren we ze.',
        'De ontvangers bewaren de gegevens niet langer dan nodig is voor de evaluatie van de pilot en de bevestiging van de resultaten. Geanonimiseerde en samengevoegde resultaten, die niet tot jou of je adres te herleiden zijn, kunnen langer worden gebruikt voor onderzoek naar het stroomnet.',
      ],
    },
    {
      id: 'intrekken',
      title: 'Je toestemming intrekken',
      blocks: [
        `Je kunt je toestemming altijd intrekken door je deelname te stoppen in Mijn programma’s in ${PORTAL_NAME}, of door contact met ons op te nemen via ${C.support}.`,
        'Na het intrekken delen we geen nieuwe gegevens meer over je deelname. Gegevens die we al hebben gedeeld, blijven rechtmatig gedeeld. Het intrekken van je toestemming betekent dat je stopt met de pilot, met de gevolgen uit de Voorwaarden van Net bewust thuis laden: je deelname eindigt en je hebt geen recht op een beloning.',
      ],
    },
    {
      id: 'rechten',
      title: 'Je rechten en klachten',
      blocks: [
        'Je hebt het recht op inzage, correctie en verwijdering van je gegevens, op beperking van de verwerking en op het intrekken van je toestemming. Hoe je die rechten uitoefent, staat in de Privacyverklaring van Chargee. Je kunt ook contact opnemen met de functionaris gegevensbescherming van Chargee.',
        'Ben je niet tevreden over hoe we met je gegevens omgaan, laat het ons dan weten. Je kunt ook een klacht indienen bij de Autoriteit Persoonsgegevens.',
      ],
    },
    {
      id: 'wijzigingen',
      title: 'Wijzigingen',
      blocks: [
        `Willen we andere gegevens delen of met andere ontvangers, dan vragen we je opnieuw om toestemming. Een nieuwe revisie van deze voorwaarden publiceren we in ${PORTAL_NAME} met de datum waarop ze ingaat.`,
      ],
    },
  ],
}
